import { apiClient, ApiClient } from './client';
import type { PublicUserProfile, PrivateUserProfile, UpdateUserProfilePayload } from './types';

export class UserProfile {
  private readonly apiClient: ApiClient;
  public readonly username: string;
  public display_name: string;
  public icon: string;

  constructor(username: string, apiClient: ApiClient, details: PublicUserProfile) {
    this.username = username;
    this.apiClient = apiClient;
    this.display_name = details.display_name;
    this.icon = details.icon;
  }

  async get(): Promise<UserProfile> {
    try {
      const response = await this.apiClient.get(`/users/${this.username}/`);
      return new UserProfile(this.username, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      throw error;
    }
  }

  async update(data: UpdateUserProfilePayload): Promise<UserProfile> {
    try {
      console.log('UserProfile.update called with data:', Object.keys(data));
      
      const formData = new FormData();
      if (data.display_name) formData.append('display_name', data.display_name);
      if (data.username) formData.append('username', data.username);
      if (data.icon) {
        console.log('Appending icon to formData, file type:', data.icon instanceof File ? 'File' : typeof data.icon);
        formData.append('icon', data.icon);
      }

      console.log('Sending user profile update request to /user/ endpoint');
      console.log('FormData entries:', [...formData.entries()].map(([key]) => key));
      
      const response = await this.apiClient.patch('/user/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('User profile update response:', response.data);
      console.log('New icon URL:', response.data.icon);
      
      return new UserProfile(
        response.data.username,
        this.apiClient,
        response.data
      );
    } catch (error) {
      console.error('Failed to update user profile:', error);
      throw error;
    }
  }
}

export const users = {
  async get(username: string): Promise<UserProfile> {
    try {
      const response = await apiClient.get(`/users/${username}/`);
      return new UserProfile(username, apiClient, response.data);
    } catch (error) {
      console.error('Failed to fetch public user profile:', error);
      throw error;
    }
  }
};

export const user = {
  async get(): Promise<PrivateUserProfile> {
    try {
      const response = await apiClient.get('/user/');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch private user profile:', error);
      throw error;
    }
  },

  async update(data: UpdateUserProfilePayload): Promise<PrivateUserProfile> {
    try {
      console.log('user.update called with data:', Object.keys(data));
      
      const formData = new FormData();
      if (data.display_name) formData.append('display_name', data.display_name);
      if (data.username) formData.append('username', data.username);
      if (data.icon) {
        console.log('Appending icon to formData via user.update');
        formData.append('icon', data.icon);
      }
      if (data.default_persona) formData.append('default_persona', data.default_persona);

      const response = await apiClient.patch('/user/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('User update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to update user profile:', error);
      throw error;
    }
  }
};

// Export type for use in type annotations
export type UserProfileType = UserProfile;
